import { Button } from '@almbrand/button';
import { Paragraph } from '@almbrand/paragraph';
import { RichText } from '@almbrand/richtext';
import { TrackingEvent } from '@almbrand/trackinghooks';
import { ArrowButton, PreviewButton } from 'components/1-atoms';
import { ThemeNames } from 'constantsValues/themeNames';
import { useThemeName } from 'hooks/useThemeName';
import { useState } from 'react';
import styles from './MultiColItemCard.module.scss';

export interface Item {
	id: string; // Unique identifier for the policy/message/other
	icon: string; // Icon identifier, e.g., 'car', 'building'
	title: string; // Main title for the policy/message/other
	subtitle?: string; // Optional subtitle, e.g., address or description
	price?: string; // Optional price, e.g., '8.599 kr/år'
	content_type?: string; // Optional content type, e.g., 'pdf', 'image'
}

export interface MultiColItemCardProps {
	items: Item[]; // Array of insurance or message data
	title: string; // Title for the list
	productIcon?: {
		// Adjusted prop for flexible icon handling
		src: string;
		alt: string;
	};
	onMoreClick?: () => void; // Callback when "+ {count} mere" is clicked
	moreClickDestination?: string; // The destination for the "more" link - tracking purposes
	actionIcon?: string; // Optional icon URL
	actionBgColor?: string; // Optional background color for the action circle
	onItemClick?: (item: Item) => void; // Generic callback for item click
	showError?: boolean; // Status code to handle error scenarios
	errorMessage?: string; // Optional message to display when there is no content
}

export const MultiColItemCard: React.FC<MultiColItemCardProps> = ({
	items: products,
	title,
	productIcon, // Updated prop for icon handling
	onMoreClick,
	moreClickDestination,
	showError,
	onItemClick,
	errorMessage,
}) => {
	const rootUrl = window.location.origin;
	const visibleItems = products?.slice(0, 3); // Show the first 3 items
	const remainingCount = products?.length - 3; // Calculate the remaining count
	const themeName = useThemeName();
	const isAlmbrand = themeName === ThemeNames.almbrand;

	const trackingData: TrackingEvent = {
		event: 'trackNavigation',
		navigationAction: title ?? '',
		navigationDestination: `${rootUrl + moreClickDestination}`,
		navigationType: 'call-to-action',
	};

	const [hoverStyle, setHoverStyle] = useState<Item | null>(null);

	// Handle item click and call the parent callback
	const handleClick = (item: Item) => {
		onItemClick?.(item); // Invoke the callback passed from the parent
	};

	const rootClassName = [styles.InsuranceList, isAlmbrand ? styles['InsuranceList--almbrand'] : '']
		.filter(Boolean)
		.join(' ');

	return (
		<div className={rootClassName}>
			<div className={styles.InsuranceList__header}>
				<Paragraph
					text={title}
					paragraphType={{ kind: 'title', type: 'title' }}
					fontColor='primaryDark'
				/>
				{!showError && (
					<Button
						buttonType='tertiary'
						size='small'
						onClick={(e) => {
							e.preventDefault();
							onMoreClick?.();
						}}
						trackingEvent={trackingData}
					>
						Se alle
					</Button>
				)}
			</div>

			{/* Accent line for Almbrand theme */}
			{isAlmbrand && <div className={styles.InsuranceList__accentLine} />}

			{/* Flex container for the list */}
			<div className={styles.InsuranceList__tileContainer}>
				{showError && (
					<div className={styles.InsuranceList__errorContainer}>
						<RichText description={errorMessage} />
					</div>
				)}

				{visibleItems &&
					visibleItems?.map((item, index) => (
						<div
							key={index}
							className={styles.InsuranceList__tile}
						>
							<div
								className={styles.InsuranceList__tileContent}
								onClick={() => handleClick(item)} // Trigger the callback on click
								onMouseEnter={() => setHoverStyle(item)}
								onMouseLeave={() => setHoverStyle(null)}
							>
								{/* Conditionally render the icon */}
								{productIcon && (
									<img
										src={productIcon.src}
										alt={productIcon.alt}
										className={styles.InsuranceList__icon}
									/>
								)}

								{/* Display item details */}
								<div className={styles.InsuranceList__textContent}>
									<Paragraph
										text={item.title}
										paragraphType={{ kind: 'paragraph', size: 'default' }}
										fontColor={hoverStyle === item ? 'primary' : 'primaryDark'}
									/>
									{item.subtitle && (
										<Paragraph
											text={item.subtitle}
											paragraphType={{ kind: 'paragraph', size: 'default' }}
											fontColor='secondaryDarkGrey'
										/>
									)}
								</div>

								{item.price && (
									<div className={styles.InsuranceList__price}>
										<Paragraph
											text={item.price}
											paragraphType={{ kind: 'paragraph', size: 'default' }}
											fontColor='primaryDark'
										/>
									</div>
								)}

								{/* Action button: arrow for Almbrand, default PreviewButton otherwise */}
								{isAlmbrand ? <ArrowButton /> : <PreviewButton />}
							</div>
						</div>
					))}
			</div>

			{!isNaN(remainingCount) && remainingCount > 0 && (
				<div className={styles.InsuranceList__moreLink}>
					<Button
						buttonType='link'
						onClick={(e) => {
							e.preventDefault();
							onMoreClick?.();
						}}
						trackingEvent={trackingData}
					>
						{isAlmbrand ? `Se ${remainingCount} mere` : `+ ${remainingCount} mere`}
					</Button>
				</div>
			)}
		</div>
	);
};
